/**
 * One failure, one row (ADR 0059 decision 2): runs with the same cause and the same source fold
 * into "2 runs of <title> failed" with the cause beside it; the row opens to list each run.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@orgus/protocol';
import { AgentOrb, GroupChevron, Pressable, RelativeTime, cn, formatInt } from '@orgus/ui';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import type { FailureFold } from './failures.ts';
import { foldTitle } from './failures.ts';
import { COL } from './SessionRow.tsx';
import { MissionChip, useSessionTitle } from './title.tsx';

export function FailureFoldRow({ fold, renderRun }: { fold: FailureFold; renderRun: (s: Session) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const title = useSessionTitle()(fold.head).title;
  const full = foldTitle(fold, title);
  const why = full.slice(full.indexOf(' failed: ') + ' failed: '.length);
  return (
    <li className="flex flex-col">
      <Pressable
        data-fold-row=""
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-row items-center gap-3 rounded-control px-row-x transition-colors duration-(--duration-fast) hover:bg-hover"
      >
        <span className="flex w-5 shrink-0 justify-center" title="Failed">
          {/* The same ring as the runs it folds (SessionRow's AgentOrb). */}
          <AgentOrb state="failed" size={20} icon={<AgentGlyph kind={fold.head.agentKind} />} label="Failed" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden" title={full}>
          <span className="min-w-0 shrink truncate text-ui font-medium text-fg-muted sm:min-w-24">
            <span className="num">{formatInt(fold.runs.length)}</span> runs of {title} failed
          </span>
          <GroupChevron open={open} />
          {fold.missionId && <MissionChip className="hidden shrink-0 md:inline-flex" />}
          <span data-meta="" className="hidden min-w-24 flex-1 truncate text-caption text-fg-subtle sm:block">
            {why}
          </span>
        </span>
        <span className={cn(COL.active, 'shrink-0 truncate text-right text-caption text-fg-subtle num')}>
          <RelativeTime ts={fold.head.lastActivity} />
        </span>
      </Pressable>
      {open && <ul className="flex flex-col pl-6">{fold.runs.map((s) => renderRun(s))}</ul>}
    </li>
  );
}
